import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  Document,
    Policy,
  PolicyExpedient,
  PolicyReceipt,
  PolicySinister
} from '../../models/policy';
import { PolicyDeath } from '../../models/policy-death';
import { PendingReceipt } from '../../models/receipt';
import { padZeros } from '../../utils/utils';
import { BaseService } from '../core/base.service';
import { MMobileHelperService } from '../core/mmobile-helper.service';
import { OperationRegisterService } from '../core/operation-register.service';

@Injectable({
  providedIn: 'root'
})
export class DeathPoliciesService {
  constructor(
    private http: HttpClient,
    private base: BaseService,
    private mmobileHelper: MMobileHelperService,
    private or: OperationRegisterService
  ) {}

  getDeathPolicy({ policyNumber, type }: PolicyDeath): Observable<PolicyDeath> {
    const url = `${this.base.getPrivateOIMBaseUrl()}/homeOtherPolicies/${policyNumber}`;
    return this.http
      .get<PolicyDeath>(
        url,
        this.base.getPrivateOIMHeaders({ cacheResponse: true })
      )
      .pipe(
        map(policy => {
          const extraData = (policy.riskInfo || [{}])[0].infoAdicional;
          const { valor } = extraData.find(
            ({ clave }) => clave === 'COD_POS_INHU'
          );
          return {
            ...policy,
            insured: {
              ...policy.insured,
              postalCode: valor
            }
          } as any;
        })
      );
  }

  filterDeathPolicy(policy) {
    const companyId = padZeros(policy.companyId, 2);
    const department = padZeros(policy.department, 3);
    const deathCard = this.mmobileHelper.getCustomConfigData(
      'tarjetas',
      'tarjetaDecesos'
    );
    return (
      deathCard.ramo.some(d => d === department) &&
      deathCard.CIAS.some(c => c === companyId)
    );
  }

  getDeathReceipts(policy: PolicyDeath): Observable<PolicyReceipt[]> {
    const url = `${this.base.getPrivateOIMBaseUrl()}/homeOtherPolicies/${
      policy.policyNumber
    }/receipts?companyId=${policy.companyId}`;
    const params = this.or.setOtherOperationParams(policy);
    return this.http.get<PolicyReceipt[]>(url, {
      ...this.base.getPrivateOIMHeaders({ cacheResponse: true }),
      params
    });
  }

  getDeathReceipt(
    policy: PolicyDeath,
    receiptNumber: string
  ): Observable<PolicyReceipt> {
    const url = `${this.base.getPrivateOIMBaseUrl()}/homeOtherPolicies/${
      policy.policyNumber
    }/receipts/${receiptNumber}?companyId=${policy.companyId}`;
    const params = this.or.setOtherOperationParams(policy);
    return this.http.get<PolicyReceipt>(url, {
      ...this.base.getPrivateOIMHeaders({ cacheResponse: true }),
      params
    });
  }

  getDeathPendingReceipts(
    policyNumber: string,
    companyId: string,
    params: any
  ): Observable<PendingReceipt[]> {
    const url = `${this.base.getPrivateOIMBaseUrl()}/homeOtherPolicies/${policyNumber}/receiptsPayment/receipts?companyId=${companyId}`;
    const headers = this.base.getPrivateOIMHeaders({ cacheResponse: true });
    return this.http.get<PendingReceipt[]>(url, { ...headers, params });
  }

  getDeathSinisters(policy: PolicyDeath): Observable<PolicySinister[]> {
    const url = `${this.base.getPrivateOIMBaseUrl()}/homeOtherPolicies/${
      policy.policyNumber
    }/sinisters`;
    const params = this.or.setOtherOperationParams(policy);
    return this.http.get<PolicySinister[]>(url, {
      ...this.base.getPrivateOIMHeaders({ cacheResponse: true }),
      params
    });
  }

  getDeathExpedients(
    policy: PolicyDeath,
    sinisterCode: string
  ): Observable<PolicyExpedient[]> {
    const url = `${this.base.getPrivateOIMBaseUrl()}/homeOtherPolicies/${
      policy.policyNumber
    }/sinisters/${sinisterCode}/expedients`;
    const params = this.or.setOtherOperationParams(policy);
    return this.http.get<PolicyExpedient[]>(url, {
      ...this.base.getPrivateOIMHeaders({ cacheResponse: true }),
      params
    });
  }

  getDeathReceiptDocument(
    policyNumber: string,
    companyId: string,
    receiptNumber: string,
    params: HttpParams
  ): Observable<Document> {
    const url = `${this.base.getPrivateOIMBaseUrl()}/homeOtherPolicies/${policyNumber}/documents/receipts/${receiptNumber}?companyId=${companyId}`;
    const headers = this.base.getPrivateOIMHeaders({ cacheResponse: true });
    return this.http.get<Document>(url, { ...headers, params });
  }

  getDeathParticularConditions(
    policyNumber: string,
    params?: HttpParams
  ): Observable<Document> {
    const url = `${this.base.getPrivateOIMBaseUrl()}/homeOtherPolicies/${policyNumber}/documents/particularConditions`;
    const headers = this.base.getPrivateOIMHeaders({ cacheResponse: true });
    return this.http.get<Document>(url, { ...headers, params });
  }

  getDeathRenewalNotice(policy: Policy): Observable<Document> {
    const { policyNumber: homePolicyNumber } = policy || {};
    const url = `${this.base.getPrivateOIMBaseUrl()}/homeOtherPolicies/${homePolicyNumber}/documents/renewalNotice`;
    const headers = this.base.getPrivateOIMHeaders({ cacheResponse: true });
    const params = this.or.calculateDocumentROParams(policy);
    return this.http.get<Document>(
      url,
      { ...headers, params }
    );
  }
}
